import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { FETCH_LIGHTSPEED_LOCATIONS, GET_LIGHTSPEED_STORES } from './queries'
import { REMOVE_INTEGRATION } from '../../queries'
import { useLazyQuery, useMutation, useQuery } from '@apollo/client'
import { LightspeedBusiness, LightspeedStore } from './types'
import Loading from 'components/Utils/Loading'
import { StepStatus } from 'hooks/useStep'
import BusinessSection from './BusinessSection'
import LocationSection from './LocationSection'
import { useSession } from 'packages/@slerp/accounts'
import CommonHeader from '../Common/CommonHeader'
import { ManageRoot } from '../styles'
import ConnectSection from './ConnectSection'
import { PartnerName, PARTNER_SLUGS, isPartner } from '../../constants'
import { useMerchant } from 'hooks/useGlobalContext/useMerchant'
import { useMessageStore } from 'hooks/useGlobalContext/useMessageStore'

const LIGHTSPEED_PATH = `/integrations/${PARTNER_SLUGS.LIGHTSPEED}`

const ManageLightspeed = () => {
  const navigate = useNavigate()
  const { addMessage } = useMessageStore()

  const { merchant } = useSession()
  const { integrations, refetchIntegrations } = useMerchant()

  const [hasIntegrationConnected, setHasIntegrationConnected] = useState(false)

  const [isInitialized, setIsInitialized] = useState(false)
  const [selectedBusiness, setSelectedBusiness] =
    useState<LightspeedBusiness | null>(null)

  const [currentStep, setCurrentStep] = useState<number>(1)

  const [connectedLocations, setConnectedLocations] = useState<
    LightspeedStore[]
  >([])
  const [businesses, setBusinesses] = useState<LightspeedBusiness[]>([])

  const { loading: loadingBusinesses } = useQuery(FETCH_LIGHTSPEED_LOCATIONS, {
    skip: !hasIntegrationConnected,
    onCompleted: (data) => {
      setBusinesses(data.fetchLightspeedIds)
    },
    onError: () => {
      handleError()
    }
  })

  const [removeIntegration] = useMutation(REMOVE_INTEGRATION)

  const [fetchConnectedLocations, { loading: loadingLightspeedStores }] =
    useLazyQuery(GET_LIGHTSPEED_STORES, {
      fetchPolicy: 'no-cache',
      variables: { merchantId: merchant?.id },
      onCompleted: (data) => {
        const connectedLocations = data?.stores.filter(
          (store: LightspeedStore) =>
            !!store.store_integration_settings.lightspeed_business_id
        )
        setConnectedLocations(connectedLocations || [])
      }
    })

  useEffect(() => {
    setHasIntegrationConnected(
      integrations?.some((partner: { partner: { name: string } }) =>
        isPartner(partner.partner.name, PartnerName.LIGHTSPEED)
      )
    )
  }, [integrations])

  useEffect(() => {
    if (!hasIntegrationConnected) {
      setIsInitialized(true)
    }

    fetchConnectedLocations().then(() => {
      setIsInitialized(true)
    })
  }, [hasIntegrationConnected])

  useEffect(() => {
    if (!connectedLocations.length || !businesses.length) {
      return
    }

    const business = businesses.find(
      (business) =>
        business.id ===
        connectedLocations[0].store_integration_settings.lightspeed_business_id
    )

    if (business) {
      setSelectedBusiness(business)
      setCurrentStep(2)
    }
  }, [connectedLocations, businesses])

  const handleBusinessFinish = (business: LightspeedBusiness | null) => {
    if (business) {
      setCurrentStep(2)
    }

    setSelectedBusiness(business)
  }

  const handleDisconnectBusiness = () => {
    setSelectedBusiness(null)
    setCurrentStep(1)
  }

  const handleError = () => {
    if (!hasIntegrationConnected) {
      return
    }

    addMessage({
      type: 'message',
      content: 'Your session has expired. Please log in again.'
    })

    navigate('/integrations')
  }

  const handleSelectLocation = (location: LightspeedStore) => {
    navigate(`${LIGHTSPEED_PATH}/store/${location.id}`)
  }

  const handleConnectStore = () => {
    navigate(
      `${LIGHTSPEED_PATH}/store/connect?businessId=${selectedBusiness?.id}`
    )
  }

  const handleBulkDisconnect = () => {
    fetchConnectedLocations()
  }

  const handleBulkSyncMenu = async () => {
    await fetchConnectedLocations()
  }

  const handleRemoveIntegration = async () => {
    await removeIntegration({
      variables: { partnerName: PartnerName.LIGHTSPEED }
    })

    refetchIntegrations()

    addMessage({
      type: 'message',
      content: 'Integration removed successfully!'
    })
  }

  if (!isInitialized || loadingBusinesses) {
    return <Loading />
  }

  if (!hasIntegrationConnected) {
    return (
      <ManageRoot>
        <CommonHeader
          title='Connect Lightspeed'
          onBack={() => navigate('/integrations')}
          helpLink='https://support.slerp.com/knowledge/how-do-i-connect-to-lightspeed-k-series'
        />
        <ConnectSection />
      </ManageRoot>
    )
  }

  return (
    <ManageRoot>
      <CommonHeader
        title='Connect Lightspeed'
        onBack={() => navigate('/integrations')}
        helpLink='https://support.slerp.com/knowledge/how-do-i-connect-to-lightspeed-k-series'
      />
      <BusinessSection
        businesses={businesses}
        selectedBusiness={selectedBusiness}
        onFinish={handleBusinessFinish}
        stepStatus={
          currentStep === 1 ? StepStatus.active : StepStatus.completed
        }
        onDisconnect={handleDisconnectBusiness}
        canDisconnect={connectedLocations.length === 0}
        onRemoveIntegration={handleRemoveIntegration}
      />
      {currentStep > 1 && !!selectedBusiness && (
        <LocationSection
          connectedLocations={connectedLocations}
          onConnect={handleConnectStore}
          onSelect={handleSelectLocation}
          stepStatus={StepStatus.active}
          onBulkDisconnect={handleBulkDisconnect}
          onBulkSyncMenu={handleBulkSyncMenu}
          isLoading={loadingLightspeedStores}
        />
      )}
    </ManageRoot>
  )
}

export default ManageLightspeed
